import { Button, Grid, Stack } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import type {
  EditOrganizationUnit,
  ModelState,
  OrganizationUnit,
  OrganizationUnitProperties
} from 'BrokerTsLib/src/dtos'
import {
  BadRequestError
} from 'BrokerTsLib/src/dtos'
import { OrganizationUnitType, Product } from 'brokertslib/src/enums'
import { useGetAcquisitionNumbers } from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { FormProvider, useForm, useFormContext } from 'BrokerTsLib/src/rhf'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import type { Ref } from 'react'
import React, { forwardRef, useEffect } from 'react'
import { useNavigate } from 'react-router'

import { CustomWaypoint } from '@/components'
import {
  OrganizationAcquisitionNumbersFormSection,
  OrganizationAppSettingsFormSection,
  OrganizationInfoFormSection,
  OrganizationLoginSettingsFormSection,
  OrganizationMotivationFormSection,
  OrganizationPropertyInsuranceFormSection,
  OrganizationVehicleInsuranceFormSection,
} from '@/components/Organization/FormSections'
import { OrganizationTravelInsuranceFormSection } from '@/components/Organization/FormSections/OrganizationTravelInsuranceFormSection'
import { usePartner } from '@/context/PartnerContext'
import { useSidebar } from '@/context/SidebarContext'
import { useAvailableProducts } from '@/hooks/useProducts'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { OrganizationVehicleLeasingInsuranceFormSection } from '@/components/Organization/FormSections/OrganizationVehicleLeasingInsuranceFormSection'
import { ArrowLeft } from '../../icons'

interface OrganizationUnitEditFormProps {
  organization: OrganizationUnit
  properties: OrganizationUnitProperties
  onSubmit: (
    values: EditOrganizationUnit,
  ) => Promise<OrganizationUnit | AxiosError>
  isCreate?: boolean
}

export const OrganizationForm = ({
  organization,
  properties,
  onSubmit,
  isCreate,
}: OrganizationUnitEditFormProps) => {
  const methods = useForm<EditOrganizationUnit>({
    defaultValues: {
      ...organization,
      properties: { ...properties },
      acquisitionNumbers: [],
    },
  })

  const { handleSubmit, watch, setError, reset } = methods

  const { data: acquisitionNumbersData } = useGetAcquisitionNumbers(
    organization.id,
    organization.superiorOrganizationUnitId,
  )

  useEffect(() => {
    if (acquisitionNumbersData) {
      reset((value) => ({ ...value, acquisitionNumbers: acquisitionNumbersData.data }))
    }
  }, [acquisitionNumbersData])
  const { pageSections, setPageSections } = useSidebar()
  const { currentPartner } = usePartner()
  const products = useAvailableProducts()

  const organizationType = watch('type')
  const acquisitionNumbers = watch('acquisitionNumbers')

  const onFormSubmit = (data: EditOrganizationUnit) =>
    onSubmit(data).then((response) => {
      const e = response as AxiosError
      if (e.response?.status === 400)
        setModelStateErrors(
          setError,
          new BadRequestError(
            e.response.data as ModelState<EditOrganizationUnit>,
          ),
        )
    })

  useEffect(() => {
    const array: Section[] = []
    let i = 1

    array.push({
      id: 'organizationInfo',
      sectionTitle: 'Informace o organizaci',
      index: i++,
      children: (
        <CustomWaypoint sectionId="organizationInfo">
          <OrganizationInfoFormSection
            id="organizationInfo"
            number={i - 1}
            organization={organization}
            isCreate={isCreate}
          />
        </CustomWaypoint>
      ),
    })

    if (
      currentPartner?.partner === 'Jap' &&
      organizationType === OrganizationUnitType.Partner
    ) {
      array.push({
        id: 'motivation',
        sectionTitle: 'Zařazení',
        index: i++,
        children: (
          <CustomWaypoint sectionId="motivation">
            <OrganizationMotivationFormSection id="motivation" number={i - 1} />
          </CustomWaypoint>
        ),
      })
    }

    if (acquisitionNumbers?.length > 0) {
      array.push({
        id: 'acquisitionNumbers',
        sectionTitle: 'Ziskatelská čísla',
        index: i++,
        children: (
          <CustomWaypoint sectionId="acquisitionNumbers">
            <OrganizationAcquisitionNumbersFormSection
              id="acquisitionNumbers"
              number={i - 1}
            />
          </CustomWaypoint>
        ),
      })
    }

    if (products.some(x => x !== Product.VehicleLeasingInsurance)) {
      array.push({
        id: 'applicationSettings',
        sectionTitle: 'Nastavení aplikace',
        index: i++,
        children: (
          <CustomWaypoint sectionId="applicationSettings">
            <OrganizationAppSettingsFormSection
              id="applicationSettings"
              number={i - 1}
            />
          </CustomWaypoint>
        ),
      })
    }

    if (products.some((x) => x === Product.VehicleInsurance)) {
      array.push({
        id: 'vehicleSettings',
        sectionTitle: 'Nastavení pojištění vozidel',
        index: i++,
        children: (
          <CustomWaypoint sectionId="vehicleSettings">
            <OrganizationVehicleInsuranceFormSection
              id="vehicleSettings"
              number={i - 1}
            />
          </CustomWaypoint>
        ),
      })
    }

    if (products.some((x) => x === Product.VehicleLeasingInsurance)) {
      array.push({
        id: 'vehicleLeasingSettings',
        sectionTitle: 'Nastavení pojištění vozidel - leasing',
        index: i++,
        children: (
          <CustomWaypoint sectionId="vehicleLeasingSettings">
            <OrganizationVehicleLeasingInsuranceFormSection
              id="vehicleLeasingSettings"
              number={i - 1}
            />
          </CustomWaypoint>
        ),
      })
    }

    if (products.some((x) => x === Product.PropertyInsurance)) {
      array.push({
        id: 'propertySettings',
        sectionTitle: 'Nastavení pojištění majetku a odpovědnosti',
        index: i++,
        children: (
          <CustomWaypoint sectionId="propertySettings">
            <OrganizationPropertyInsuranceFormSection
              id="propertySettings"
              number={i - 1}
            />
          </CustomWaypoint>
        ),
      })
    }

    if (products.some((x) => x === Product.TravelInsurance)) {
      array.push({
        id: 'travelSettings',
        sectionTitle: 'Nastavení cestovního pojištění',
        index: i++,
        children: (
          <CustomWaypoint sectionId="travelSettings">
            <OrganizationTravelInsuranceFormSection
              id="travelSettings"
              number={i - 1}
            />
          </CustomWaypoint>
        ),
      })
    }

    array.push({
      id: 'loginsSettings',
      sectionTitle: 'Nastavení loginů a hesel',
      index: i++,
      children: (
        <CustomWaypoint sectionId="loginsSettings">
          <OrganizationLoginSettingsFormSection
            ouId={organization.id}
            id="loginsSettings"
            number={i - 1}
          />
        </CustomWaypoint>
      ),
    })

    array.push({
      id: 'footer',
      title: 'Konec',
      sectionTitle: 'Konec',
      isHidden: true,
      index: i++,
      children: (
        <CustomWaypoint sectionId="footer">
          <OrganizationEditFormFooter
            isCreate={isCreate}
            organization={organization}
          />
        </CustomWaypoint>
      ),
    })

    setPageSections(array)

    return () => setPageSections([])
  }, [organization, organizationType, acquisitionNumbers, setPageSections])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Stack gap={8}>
          {pageSections.map((x) => (
            <React.Fragment key={x.id}>{x.children}</React.Fragment>
          ))}
        </Stack>
      </form>
    </FormProvider>
  )
}

const OrganizationEditFormFooter = forwardRef(
  function OrganizationEditFormFooter(
    {
      isCreate,
      organization,
    }: {
      isCreate: boolean | undefined
      organization: OrganizationUnit
    },
    ref: Ref<HTMLDivElement>,
  ) {
    const navigate = useNavigate()

    const {
      formState: { isSubmitting },
    } = useFormContext<EditOrganizationUnit>()

    return (
      <Grid
        id="footer"
        ref={ref}
        sx={{
          width: '100%',
          gridTemplateColumns: 'auto auto',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant="ghost"
          isLoading={isSubmitting}
          type="button"
          onClick={() =>
            navigate(
              '/organization/' +
              (organization.id ??
                organization.superiorOrganizationUnitId ??
                'list'),
            )
          }
        >
          <ArrowLeft color="secondary.500" />
          Zpět
        </Button>
        <Button colorScheme="primary" isLoading={isSubmitting} type="submit">
          {isCreate ? 'Vytvořit organizaci' : 'Uložit'}
        </Button>
      </Grid>
    )
  },
)
